import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Camera, Sparkles, FileSignature, Users, BarChart3, Zap, Star, ShieldCheck, Clock } from "lucide-react";
import { BRAND_COPY, useTradeMode, getPrimaryTrade } from "@/config/trade";
import { GoogleReviewsWidget } from "@/components/GoogleReviewsWidget";

const trades = [
  { slug: "hvac", name: "HVAC" },
  { slug: "plumbing", name: "Plumbing" },
  { slug: "electrical", name: "Electrical" },
  { slug: "roofing", name: "Roofing" },
  { slug: "handyman", name: "Handyman" },
  { slug: "landscaping", name: "Landscaping" },
];

const features = [
  { icon: Camera, title: "AI Photo-to-Quote", desc: "Customers snap a photo. AI returns Good/Better/Best in 60 seconds." },
  { icon: Users, title: "Built-in CRM", desc: "Contacts, pipeline, calendar, tasks — all in one admin." },
  { icon: FileSignature, title: "E-Sign Order Forms", desc: "Send branded order forms, collect signatures remotely or in-person." },
  { icon: Zap, title: "Automation", desc: "Email, SMS, GHL sync, Slack alerts — fires the moment leads come in." },
  { icon: BarChart3, title: "Analytics & Pipeline", desc: "Conversion, source, and revenue insights baked into the dashboard." },
  { icon: Sparkles, title: "Trade-Mode Switcher", desc: "One platform — re-skin instantly for HVAC, plumbing, electrical, roofing, more." },
];

const testimonials = [
  { quote: "ProTradeOS replaced our website AND ServiceTitan add-ons. We're closing 28% more leads.", name: "Marcus R.", role: "Owner, Texas HVAC Co." },
  { quote: "The AI photo quote tool blew my mind. Customers love getting an instant ballpark.", name: "Dani S.", role: "Plumbing Pros LLC" },
  { quote: "Spun up our entire site, CRM, and quoting flow in 9 days. Genuinely impressive.", name: "Jordan K.", role: "Bright Spark Electrical" },
];

const Index = () => {
  const mode = useTradeMode();
  const primaryTrade = getPrimaryTrade();
  const copy =
    primaryTrade && mode !== "all"
      ? BRAND_COPY[mode]
      : {
          eyebrow: "AI Contractor OS Template",
          headline: "ProTradeOS — AI Contractor OS for Home Service Trades",
          sub: "Professional website + AI photo-to-quote + CRM + e-sign orders. Launch as HVAC, Plumbing, Electrical or any trade in one click.",
        };

  const seoTitle = primaryTrade
    ? `${primaryTrade.title} | ProTradeOS`
    : "ProTradeOS — AI Contractor OS for Home Service Trades";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        seo={{
          title: seoTitle,
          description: primaryTrade?.metaDescription ?? copy.sub,
          keywords: ["contractor software", "HVAC CRM", "plumbing CRM", "AI quoting", "home services platform"],
        }}
        path="/"
      />
      <Navigation />

      {/* HERO */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-sm font-semibold text-primary mb-4 tracking-wider uppercase">{copy.eyebrow}</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground leading-tight">{copy.headline}</h1>
          <p className="text-lg md:text-xl text-gray-700 font-medium mb-8 leading-relaxed max-w-3xl mx-auto">{copy.sub}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-lg px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ai-quote"><Camera className="w-5 h-5 mr-2" /> Try the AI Photo Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 h-auto border-accent text-accent hover:bg-accent hover:text-accent-foreground">
              <Link to={primaryTrade ? `/services/${primaryTrade.slug}` : "/services"}>
                {primaryTrade ? `Explore ${primaryTrade.name} Services` : "Explore Services"}
              </Link>
            </Button>
          </div>

          {/* Trust badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 font-medium">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Licensed & Insured</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Same-Day Service</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-accent" /> 4.9/5 Avg Rating</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Trusted by 500+ Contractors</span>
          </div>
        </div>
      </section>

      {/* AI QUOTE TEASER */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <Card className="border-primary/20 shadow-lg">
            <CardContent className="p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider bg-accent/10 text-accent rounded-full mb-3">New · AI Powered</span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Get a quote in 60 seconds — just snap a photo.</h2>
                <p className="text-gray-700 font-medium mb-6">Upload a photo of your AC unit, leak, panel, or roof. Our AI returns a Good/Better/Best estimate before a tech ever rolls a truck.</p>
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ai-quote"><Camera className="w-5 h-5 mr-2" /> Upload a Photo →</Link>
                </Button>
              </div>
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center border-2 border-dashed border-primary/30">
                <Camera className="w-20 h-20 mx-auto text-primary mb-4" />
                <p className="text-sm text-gray-700 font-medium">Drop a photo here or open the AI Quote tool</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Everything home service contractors need</h2>
            <p className="text-lg text-gray-700 font-medium">Marketing, quoting, CRM, e-sign, automation — one platform, not seven.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <Card key={f.title} className="border-border hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <f.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{f.title}</h3>
                  <p className="text-gray-700 font-medium text-sm">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SELF-LEARNING AI TEASER */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardContent className="p-8 text-center">
              <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Your AI assistant that improves the more you use it</h3>
              <p className="text-gray-700 font-medium">Every thumbs up teaches it what your customers love. Every thumbs down sharpens its answers. Self-learning, built in.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* TRADES */}
      <section className="py-16 bg-gradient-to-b from-transparent to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-3">Built for every home service trade</h2>
          <p className="text-gray-700 font-medium mb-8">Pick your trade — the platform reskins instantly.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {trades.map((t) => (
              <Button asChild key={t.slug} variant="outline" className="border-primary/30 text-foreground hover:bg-primary hover:text-primary-foreground">
                <Link to={`/services/${t.slug}`}>{t.name}</Link>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Contractors are growing faster with ProTradeOS</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <Card key={t.name} className="border-border">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-accent text-accent" />)}
                  </div>
                  <p className="text-gray-700 font-medium italic mb-4">"{t.quote}"</p>
                  <div className="text-sm">
                    <div className="font-bold text-foreground">{t.name}</div>
                    <div className="text-gray-600">{t.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* GOOGLE REVIEWS */}
          <div className="mt-10 max-w-4xl mx-auto">
            <GoogleReviewsWidget />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">Ready to modernize your contractor business?</h2>
          <p className="text-lg text-gray-700 font-medium mb-8">Get an instant AI quote, or see the full platform in action.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="text-lg px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ai-quote"><Camera className="w-5 h-5 mr-2" /> Get Service Now</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 h-auto border-accent text-accent hover:bg-accent hover:text-accent-foreground">
              <Link to="/contact-us">Talk to Our Team</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
